import { isEqual } from 'lodash';

import { genGUID } from '@helpers';

const removeOldItemsFromRender = ({
  alreadyRendered,
  originalItem,
  newItems,
  memoKey,
}) => (
  newItems.some(item => isEqual(item, originalItem))
    ? { refresh: false }
    : {
      renderItems: alreadyRendered.filter(({ _key }) => _key !== memoKey),
      refresh: true,
    }
);

// prepareDataHandler - callback for prepare raw data from `getItemsByName`
export const createMemoWrapper = ({ prepareDataHandler = v => v } = {}) => {
  let alreadyRendered = {};
  let originalItems = {};

  return {
    addItems: (itemsKey, items, {
      oldItemsRenderHandler = removeOldItemsFromRender,
      memoKeyGenerator,
    } = {}) => {
      let refresh = false;

      if (!alreadyRendered) {
        alreadyRendered = {};
      }
      if (!alreadyRendered[itemsKey]) {
        alreadyRendered[itemsKey] = [];
      }

      items.forEach((item) => {
        const key = memoKeyGenerator(item);

        if (!alreadyRendered[itemsKey].some(({ _key }) => _key === key)) {
          originalItems[key] = { itemsKey, dataStr: JSON.stringify(item) };

          alreadyRendered[itemsKey].push({
            ...item,
            _key: key,
            _id: genGUID(),
          });

          refresh = true;
        }
      });

      // remove old values
      Object.keys(originalItems)
        .filter(key => originalItems[key].itemsKey === itemsKey)
        .forEach((key) => {
          const render = oldItemsRenderHandler({
            originalItem: JSON.parse(originalItems[key].dataStr),
            alreadyRendered: alreadyRendered[itemsKey],
            newItems: items,
            memoKey: key,
            itemsKey,
          });

          if (render.refresh) {
            alreadyRendered[itemsKey] = render.renderItems;
            delete originalItems[key];
          }

          refresh = refresh || render.refresh;
        });

      return refresh;
    },

    getItemsByName: itemsKey => prepareDataHandler(
      JSON.parse(JSON.stringify(alreadyRendered[itemsKey] || [])),
    ),

    getAllItems: () => JSON.parse(JSON.stringify(alreadyRendered || {})),

    clearMemo: () => {
      alreadyRendered = {};
      originalItems = {};
    },

    destroyMemo: () => {
      alreadyRendered = null;
    },
  };
};
